import React, {Ref, useEffect, useState} from 'react';
import EditTemplateRenderer from './EditTemplateRenderer';
import {JobTemplate as JobTemplateInterface, BlockBase, Block, Benefit, MediaElement} from '../interfaces';
import TextBlockTemplateSettingsDialog from '../dialogs/template/TextBlockTemplateSettingsDialog';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import BenefitTemplateSettingsDialog from '../dialogs/template/BenefitTemplateSettingsDialog';
import ApplyButtonTemplateSettingsDialog from '../dialogs/template/ApplyButtonTemplateSettingsDialog';
import {updateBlock, deleteBlock, getUsedContentTypes, filterReferencedFiles} from '../utils';
import TemplateCssDialog from '../dialogs/template/TemplateCssDialog';
import ShadowDomWrapper from './ShadowDomWrapper';
import {LocaleProvider} from '../context/localeContext';
import ContactBlockTemplateSettingsDialog from "../dialogs/template/ContactBlockTemplateSettingsDialog.tsx";
import MediaBlockTemplateSettingsDialog from "../dialogs/template/MediaBlockTemplateSettingsDialog";
import ContainerBlockTemplateSettingsDialog from "../dialogs/template/ContainerBlockTemplateSettingsDialog.tsx";
import HeaderBlockTemplateSettingsDialog from "../dialogs/template/HeaderBlockTemplateSettingsDialog.tsx";
import {CssStylingService} from '../css-styling-service';
import {applyTranslationOverrides, TranslationOverrides} from '../i18n/config';

const blockNames: Record<string, string> = {
  introduction: 'Introduction',
  contractText: 'Contract Information',
  qualificationsText: 'Qualifications',
  responsibilitiesText: 'Responsibilities',
  requirementsText: 'Requirements',
  benefitsText: 'Benefits',
  customText: 'Custom Text',
  image: 'Image/Video',
  container: 'Container',
  applyButton: 'Apply Button',
  benefits: 'Benefits List',
  header: 'Header'
}

const availableBlocks: BlockBase<any, any>[] = [
  {
    id: 'introduction',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'introduction',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Introduction',
        content: '<p>Introduction</p>',
      }
    }
  },
  {
    id: 'contractText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'contract',
      withHeader: false,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        content: '<p>Contract info</p>',
      }
    }
  },
  {
    id: 'qualificationsText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'qualifications',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Qualifications',
        content: '<ul><li>Qualification</li></ul>',
      }
    }
  },
  {
    id: 'responsibilitiesText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'responsibilities',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Responsibilities',
        content: '<ul><li>Responsibility</li></ul>'
      }
    }
  },
  {
    id: 'requirementsText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'requirements',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Requirements',
        content: '<ul><li>Requirement</li></ul>'
      }
    }
  },
  {
    id: 'benefitsText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'benefits',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Benefits',
        content: '<ul><li>Benefits</li></ul>'
      }
    }
  },
  {
    id: 'customText',
    type: 'textBlock',
    version: 'v1',
    properties: {
      contentType: 'customText',
      withHeader: true,
      headerLevel: 2
    },
    defaultContent: {
      en: {
        headerText: 'Custom Text',
        content: '<p>Custom text</p>'
      }
    }
  },
  {
    id: 'image',
    type: 'mediaBlock',
    version: 'v1',
    properties: {
      defaultType: 'image'
    },
    defaultContent: {
      en: {
        src: 'https://via.placeholder.com/150',
        altText: 'placeholder image',
        type: 'image'
      }
    }
  },
  {
    id: 'container',
    type: 'container',
    version: 'v1',
    properties: {
      layout: [50, 50],
      children: [[], []]
    }
  },
  {
    id: 'applyButton',
    type: 'applyButtonBlock',
    version: 'v1',
    properties: {},
    defaultContent: {
      en: {
        label: 'Apply Now'
      }
    }
  },
  {
    id: 'benefits',
    type: 'benefits',
    version: 'v1',
    properties: {
      columns: 2
    },
  },
  {
    id: 'header',
    type: 'headerBlock',
    version: 'v1',
    properties: {
      headerLevel: 1
    },
    defaultContent: {
      en: {
        headerText: 'Header'
      }
    }
  }
];

interface TemplateEditorProps {
  ref: Ref<HTMLInputElement>;
  fallbackLocale: string;
  locale: string;
  template: JobTemplateInterface;
  availableLocales: string[];
  benefits?: Benefit[];
  onChange?: (value: JobTemplateInterface) => void;
  onFilesChange?: (files: MediaElement[]) => void;
  newMediaCallback?: (args: { blockId: string }) => Promise<string>;
  mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>;
  dragAndDropMediaCallback?: (args: { blockId: string; file: File }) => Promise<string>;
  files?: MediaElement[];
  namespace?: string;
  translationOverrides?: TranslationOverrides;
  primaryColor?: string;
}

// Helper function to handle CSS generation and replacement logic
const generateAndReplaceStyles = (
  cssStylingService: CssStylingService,
  styles: string | null | undefined,
  blocks: BlockBase<unknown, unknown>[]
): string => {
  const startMarker = '/*### START OF GENERATED STYLES*/';
  const endMarker = '/*### END OF GENERATED STYLES*/\n';
  
  if (styles) {
    const startIndex = styles.indexOf(startMarker);
    const endIndex = styles.indexOf(endMarker);
    
    if (startIndex !== -1 && endIndex !== -1) {
      // Extract the part before and after the generated styles
      const beforeGenerated = styles.substring(0, startIndex);
      const afterGenerated = styles.substring(endIndex + endMarker.length);

      // Generate new CSS from blocks
      const generatedStyles = cssStylingService.generateStyles(blocks);

      // Combine all parts
      return (beforeGenerated + generatedStyles + afterGenerated);
    } else {
      // If no markers found, generate new styles and append them
      const generatedStyles = cssStylingService.generateStyles(blocks);
      return generatedStyles + (styles || '');
    }
  } else {
    // No existing styles - generate new ones
    return cssStylingService.generateStyles(blocks);
  }
};
const cssStylingService = new CssStylingService();

const TemplateEditor: React.FC<TemplateEditorProps> = ({fallbackLocale, locale, template, availableLocales, benefits, onChange, onFilesChange, newMediaCallback, mediaLibraryCallback, dragAndDropMediaCallback, files, namespace = 'template-editor', translationOverrides, primaryColor = '#3b82f6'}) => {
   const [templateState, setTemplate] = useState<JobTemplateInterface>(template);
   const [settingsBlock, setSettingsBlock] = useState<Block | null>(null);
   const [blockToDelete, setBlockToDelete] = useState<Block | null>(null);
   const [isCssDialogOpen, setIsCssDialogOpen] = useState(false);
   const [isMobile, setIsMobile] = useState(false);

   // Apply translation overrides when they change
   useEffect(() => {
     applyTranslationOverrides(translationOverrides);
   }, [translationOverrides]);

   useEffect(() => {
     // Generate CSS styles based on blocks
     const generatedStyles = generateAndReplaceStyles(cssStylingService, templateState.styles, templateState.blocks);
     if (generatedStyles !== templateState.styles) {
       setTemplate(prev => ({...prev, styles: generatedStyles}));
     }
     if (onChange) {
       onChange(templateState)
     }
     // Notify about file changes
     if (onFilesChange) {
       const referencedFiles = filterReferencedFiles(templateState.blocks, files);
       onFilesChange(referencedFiles);
     }
   }, [onChange, onFilesChange, templateState])

  const handleDragStart = (e: React.DragEvent, block: BlockBase<unknown, unknown>) => {
    e.dataTransfer.setData('component', JSON.stringify(block));
  };

  // Handle block reordering
  const handleBlockReorder = (newBlocks: BlockBase<unknown, unknown>[]) => {
    setTemplate(prevTemplate => ({
      ...prevTemplate,
      blocks: newBlocks
    }));
  };

  const handleBlockSettingsSave = (updatedBlock: Block) => {
    const newBlocks = updateBlock(templateState.blocks, updatedBlock);
    setTemplate({ ...templateState, blocks: newBlocks });
    setSettingsBlock(null);
  };

  const handleBlockDelete = (deletedBlock: Block) => {
    setBlockToDelete(deletedBlock);
  };

  const confirmDelete = () => {
    if (blockToDelete) {
      const newBlocks = deleteBlock(templateState.blocks, blockToDelete.id);
      setTemplate({ ...templateState, blocks: newBlocks });
      setBlockToDelete(null);
    }
  };

  const cancelDelete = () => {
    setBlockToDelete(null);
  };

  const handleCssSave = (css: string) => {
    const updatedStyles = generateAndReplaceStyles(cssStylingService, css, templateState.blocks)
    
    setTemplate(prevTemplate => ({
      ...prevTemplate,
      styles: updatedStyles
    }));
    setIsCssDialogOpen(false);
  };

  const renderSettingsDialog = () => {
    if (!settingsBlock) return null;

    switch (settingsBlock.type) {
      case 'textBlock':
        return (
          <TextBlockTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
          />
        );
      case 'benefitsBlock':
        return (
          <BenefitTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
          />
        );
      case 'applyButtonBlock':
        return (
          <ApplyButtonTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
          />
        );
      case 'contactBlock':
        return (
          <ContactBlockTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
          />
        );
      case 'mediaBlock':
        return (
          <MediaBlockTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
            newMediaCallback={newMediaCallback}
            mediaLibraryCallback={mediaLibraryCallback}
          />
        );
      case 'container':
        return (
          <ContainerBlockTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
          />
        );
      case 'headerBlock':
        return (
          <HeaderBlockTemplateSettingsDialog
            block={settingsBlock}
            onClose={() => setSettingsBlock(null)}
            onSave={handleBlockSettingsSave}
            availableLocales={availableLocales}
            fallbackLocale={fallbackLocale}
          />
        );
      default:
        return null;
    }
  }

  const singleInstanceContentTypes = ['introduction', 'contract', 'requirements', 'qualifications', 'responsibilities', 'benefits'];
  const usedContentTypes = getUsedContentTypes(templateState.blocks);

  return (
    <ShadowDomWrapper>
      <div className="flex">
        <div className="w-[250px] p-4 overflow-y-auto">
          <h3>Blocks</h3>
          <ul className="list-none p-0">
            {availableBlocks.map((block, index) => {
              const isUsed = block.properties.contentType && usedContentTypes.includes(block.properties.contentType);
              const isDisabled = isUsed && singleInstanceContentTypes.includes(block.properties.contentType);

              return (
                <li
                  key={index}
                  className={`p-2 my-1 border-2 border-solid border-gray-300 rounded ${
                    isDisabled ? 'cursor-not-allowed opacity-35' : 'cursor-grab'
                  }`}
                  draggable={!isDisabled}
                  onDragStart={(e) => !isDisabled && handleDragStart(e, block)}
                >
                  {blockNames[block.id] || block.id}
                </li>
              );
            })}
          </ul>
          <button
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
            onClick={() => setIsCssDialogOpen(true)}
          >
            Edit CSS
          </button>
          <button
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => setIsMobile(!isMobile)}
          >
            {isMobile ? 'Desktop' : 'Mobile'}
          </button>
        </div>

        {/* Main content area with template preview */}
        <div className="flex-1 p-4 overflow-y-auto">
          <EditTemplateRenderer
            template={templateState}
            fallbackLocale={fallbackLocale}
            locale={locale}
            onBlockReorder={handleBlockReorder}
            onBlockSettingsClick={(block) => setSettingsBlock(block as Block)}
            onBlockDelete={handleBlockDelete}
            benefits={benefits}
            isMobile={isMobile}
            newMediaCallback={newMediaCallback}
            mediaLibraryCallback={mediaLibraryCallback}
            dragAndDropMediaCallback={dragAndDropMediaCallback}
            handleBlockSettingSave={handleBlockSettingsSave}
            files={files}
            namespace={namespace}
            primaryColor={primaryColor}
            availableLocales={availableLocales}
          />
        </div>
        <LocaleProvider
          availableLocales={availableLocales}
          fallbackLocale={fallbackLocale}
        >
          {renderSettingsDialog()}
        </LocaleProvider>
        {blockToDelete && (
          <ConfirmationDialog
            title="Delete Block"
            message="Are you sure you want to delete this block?"
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
          />
        )}
        {isCssDialogOpen && (
          <TemplateCssDialog
            title="Edit Template CSS"
            initialCss={templateState.styles || ''}
            onClose={() => setIsCssDialogOpen(false)}
            onSave={handleCssSave}
          />
        )}
      </div>
    </ShadowDomWrapper>
  );
};

export default TemplateEditor;